import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';

import { formaterMois, formaterMontant } from './formatAvances';
import { resumer } from './remboursements';

/**
 * Le détail d'un remboursement, dans le panneau latéral.
 *
 * Un récapitulatif en trois chiffres, la progression, puis le tableau des
 * échéances. Le montant d'un mois est celui que la Paie a réellement retenu,
 * qui peut différer de l'échéance si le plafond légal l'a écrêtée ou reportée.
 * Le statut se lit en texte, pas seulement en couleur.
 */

const BADGE_STATUT = {
  prelevee: 'bg-green-50 text-green-700 ring-green-600/20',
  calculee: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  a_venir: 'bg-slate-50 text-slate-600 ring-slate-500/20',
};

function DetailRemboursement({ demande, locale, prefixe, afficherDemandeur, onFermer }) {
  const { t } = useTranslation();
  const resume = resumer(demande);
  const titreId = `${prefixe}-detail-titre-${demande.id}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titreId}
      data-testid={`${prefixe}-detail-${demande.id}`}
      className="space-y-5"
    >
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          {afficherDemandeur && demande.demandeur?.nom && (
            <p className="text-sm text-slate-500">{demande.demandeur.nom}</p>
          )}
          <h2 id={titreId} className="text-base font-semibold text-slate-900">
            {t(`mesAvances.types.${demande.type}`, { defaultValue: demande.type })}
            <span className="font-normal text-slate-400"> · </span>
            <span className="tabular-nums">{formaterMontant(resume.montant, locale)}</span>
          </h2>
        </div>
        <button
          type="button"
          data-testid={`${prefixe}-detail-fermer`}
          onClick={onFermer}
          aria-label={t('mesAvances.fermer')}
          className="p-2 -m-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[--color-primary]"
        >
          <FaTimes aria-hidden="true" />
        </button>
      </div>

      <dl className="grid grid-cols-3 divide-x divide-slate-200 rounded-lg border border-slate-200">
        {[
          ['preleve', formaterMontant(resume.preleve, locale)],
          ['reste', formaterMontant(resume.reste, locale)],
          ['fin', resume.fin ? formaterMois(resume.fin, locale) : '—'],
        ].map(([cle, valeur]) => (
          <div key={cle} className="px-3 py-2.5">
            <dt className="text-xs text-slate-500">{t(`mesAvances.detail.${cle}`)}</dt>
            <dd className="mt-0.5 text-sm font-semibold text-slate-900 tabular-nums capitalize">{valeur}</dd>
          </div>
        ))}
      </dl>

      {resume.pourcentage !== null && (
        <div>
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>{t('mesAvances.detail.progression')}</span>
            <span className="tabular-nums">{resume.pourcentage} %</span>
          </div>
          <div
            role="progressbar"
            aria-valuenow={resume.pourcentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('mesAvances.progression', { pourcentage: resume.pourcentage })}
            className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden"
          >
            <div className="h-full rounded-full bg-[--color-primary]" style={{ width: `${resume.pourcentage}%` }} />
          </div>
        </div>
      )}

      <table className="w-full text-sm">
        <caption className="sr-only">{t('mesAvances.detail.echeances')}</caption>
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
            <th scope="col" className="py-2 font-medium">{t('mesAvances.detail.mois')}</th>
            <th scope="col" className="py-2 font-medium text-right">{t('mesAvances.detail.montant')}</th>
            <th scope="col" className="py-2 font-medium text-right">{t('mesAvances.detail.statut')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {resume.echeances.map((echeance) => (
            <tr key={echeance.mois}>
              <td className="py-2.5 capitalize text-slate-700">{formaterMois(echeance.mois, locale)}</td>
              <td className="py-2.5 text-right tabular-nums font-medium text-slate-900">
                {formaterMontant(echeance.montant, locale)}
              </td>
              <td className="py-2.5 text-right">
                <span
                  className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                    BADGE_STATUT[echeance.statut] || BADGE_STATUT.a_venir
                  }`}
                >
                  {t(`mesAvances.statuts.${echeance.statut}`, { defaultValue: echeance.statut })}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DetailRemboursement;
